/**
 * Тексты и картинки заказчика на первых экранах.
 *
 * Лежат отдельно от экранов по той же причине, что цвета лежат в теме:
 * экран не должен знать, у какого он партнёра. Обложка умеет показать
 * заголовок, пояснение, картинку и логотип — а что именно, ей передают.
 *
 * Пока это файл, а не строка в базе. Так сделано намеренно: тексты
 * первых экранов вычитываются вместе с макетом и меняются раз в жизни,
 * а переносить их в базу имеет смысл тогда, когда заказчик станет править
 * их сам. Формат уже подготовлен под такой переезд — это просто данные.
 */

/**
 * Шрифт надписи, которую человек набирает сам.
 *
 * Семейство и насыщенность нужны числом, а не мерой в стилях: надпись
 * рисуется на холсте, а холст про CSS-переменные ничего не знает.
 */
export interface TextFont {
  key: string;
  /** Как шрифт называется человеку. */
  title: string;
  family: string;
  weight: number;
}

export interface Brand {
  /**
   * Ширина макета заказчика. От неё считается масштаб и порог узкой
   * раскладки: у effective-records макет 1920, у ФОСИ — блок 1160,
   * и без этого числа блок 1160 считался бы телефоном.
   */
  designWidth: number;
  /**
   * Высота макета. Задана числом, а не «сколько получится»: виджет стоит
   * блоком на чужой странице, у блока есть высота, и содержимое обязано
   * в неё помещаться. Прокрутки внутри блока быть не должно — человек её
   * не ждёт и не найдёт.
   */
  designHeight: number;
  /** Макет телефона. Пусто — берётся общий по умолчанию. */
  mobileWidth?: number;
  mobileHeight?: number;
  /**
   * Оболочка вокруг экранов.
   *
   * `sidebar` — меню слева, шапка с полосой шагов, подвал: так устроен
   * effective-records. `compact` — только экран и полоса шагов внизу.
   */
  shell: 'sidebar' | 'compact';
  /**
   * Какие изделия продаёт заказчик. Пусто — все, что есть в каталоге.
   *
   * Нужно, пока каталог подставляется демонстрационный: у ФОСИ только
   * футболки, а в демо лежат ещё сумка и кепка, и без отбора они попадают
   * на витрину заказчика.
   */
  garments?: string[];
  /**
   * На чём работает конструктор. Пусто — на всём, что продаётся.
   *
   * Отдельно от `garments` намеренно: продавать и собирать — разные
   * списки. У СИЯЙ на витрине и худи, и кепки, а своё пока собирается
   * только на футболке: зона печати и мокапы выверены у неё одной.
   * Свалить это в один список значило бы либо убрать худи с витрины,
   * либо предложить человеку собрать принт на вещи, которой мы не умеем
   * его показать.
   */
  customGarments?: string[];
  /**
   * Цвета изделия в конструкторе.
   *
   * Отдельно от цветов товара намеренно: это два разных списка.
   *
   * У товара цвета — это то, в чём его можно купить, и первый из них
   * задаёт, каким вещь показана на витрине. Заказчик ставит туда
   * светло-серую, потому что четыре карточки должны отличаться друг
   * от друга.
   *
   * В конструкторе человек собирает вещь с нуля, и список там свой:
   * на чём мы печатаем. Светло-серой в нём нет, а белая есть.
   *
   * Пусто — конструктор берёт цвета товара, как было раньше.
   */
  customColors?: string[];
  /**
   * Корзина в шапке приложения.
   *
   * У магазина набор корзины идёт прямо с витрины, и кнопка обязана быть
   * на виду в тот момент, когда человек кладёт вещь. У конструктора
   * иначе: там собирают одну вещь и кладут её в самом конце, а корзина
   * в шапке весь путь предлагала бы заглянуть в пустое.
   */
  headerCart?: boolean;
  logoUrl?: string;
  logoMiniUrl?: string;
  /**
   * Знак в движении: заставка и плашка конструктора.
   *
   * Отдельно от `logoUrl`, потому что это другой файл, а не другой
   * размер того же: анимация весит на порядок больше и в шапке,
   * где знак стоит всегда, была бы неуместна.
   */
  splashLogoUrl?: string;
  coverImageUrl?: string;

  /**
   * Изделие на обложке: цвет ткани, принт берётся из `shopPrints`.
   *
   * Важнее `coverImageUrl` и заменяет его. Отдельный снимок на обложке
   * расходится с каталогом при первой же смене товара, а собранное
   * изделие — нет: оно собирается теми же координатами зоны печати,
   * что карточка каталога и конструктор.
   */
  coverGarment?: { color: string };
  /**
   * Принт готового изделия — свой для каждого цвета ткани.
   *
   * Голая футболка на витрине выглядит как заготовка, а человек выбирает
   * вещь, которую получит.
   *
   * Картой, а не одним файлом, потому что один и тот же рисунок печатают
   * разной краской: на розовой ткани сосиски белые, на остальных —
   * розовые. Это не оформление витрины, а два разных печатных файла,
   * и выбор между ними — свойство товара.
   *
   * Ключ `*` — для всех цветов, которых нет в карте.
   */
  shopPrints?: Record<string, string>;
  /**
   * Оплата прямо в виджете на сайте: форма доставки и касса здесь же,
   * без перехода в мини-приложение. В мини-приложении и боте оплата своя.
   *
   * NOTE: пара к WIDGET_SITE_PAYMENT_PARTNERS на сервере. Без серверной
   * настройки оформление ответит 404, и человек увидит ошибку.
   */
  sitePayment?: boolean;
  /**
   * Печать светлой краской. Такой принт виден только на тёмном изделии,
   * и витрина обязана это учитывать: на бежевой футболке белый рисунок
   * исчезает целиком, а карточка выглядит как пустая заготовка.
   */
  isShopPrintLight?: boolean;
  /** Цена изделия со своим принтом. У готовых своя, из каталога. */
  printPriceRub?: number;
  /**
   * Чем человек набирает свою надпись.
   *
   * У заказчика, а не в коде экрана: набор начертаний — это часть его
   * оформления, ровно как цвета и меры. Пусто — надписи нет вовсе,
   * и вкладка с ней не показывается.
   *
   * Каждое семейство обязано быть подключено страницей: холст рисует
   * тем, что загружено, и ненайденное начертание молча подменится
   * системным.
   */
  textFonts?: TextFont[];
  /**
   * Мини-приложение, куда уходит человек с последнего шага.
   *
   * NOTE: ждём от заказчика настоящую ссылку на бота с мини-приложением.
   */
  miniappUrl?: string;
  /** Приписка в углу обложки: чем вообще является этот модуль. */
  overline?: string;
  coverTitle: string;
  coverLead?: string;
  shopTitle?: string;
  shopLead?: string;
}

const RECORDS: Brand = {
  designWidth: 1920,
  designHeight: 1080,
  shell: 'sidebar',
  coverTitle: 'Собери свой мерч',
};

const FOSY: Brand = {
  designWidth: 1160,
  designHeight: 705,
  mobileWidth: 390,
  mobileHeight: 780,
  shell: 'compact',
  garments: ['tshirt'],
  logoUrl: '/fosy/logo.svg',
  logoMiniUrl: '/fosy/logo-mini.svg',
  // Обложка — белая футболка с котом в наушниках, по решению заказчика.
  // Это пример того, что соберёт конструктор из фото питомца, а не товар
  // каталога: готовые вещи человек увидит на следующем экране.
  // Наклон и масштаб — в _brands.scss (--ls-hero-tilt, --ls-hero-cover-scale).
  coverImageUrl: '/fosy/cover-tshirt.png',
  shopPrints: {
    // На ярко-розовой ткани розовые сосиски пропали бы в фоне —
    // там печатают всё белым.
    pink: '/fosy/print-antiworking-white.png',
    '*': '/fosy/print-antiworking-pink.png',
  },

  // Пять цветов конструктора — ровно те, что заказчик прислал снимком.
  // Светло-серой здесь нет: она нужна витрине, чтобы четвёртая карточка
  // отличалась от первой, а печатаем мы на белой.
  customColors: ['white', 'pink', 'charcoal', 'navy', 'black'],
  printPriceRub: 2700,
  sitePayment: true,
  overline: 'Бот-конструктор мерча',
  coverTitle: 'Создай уникальный мерч со своим питомцем!',
  coverLead: 'Загружай фото и выбирай цвет и шрифт надписи.',
  shopTitle: 'Наш каталог',
  shopLead: 'Можешь выбрать готовое изделие или собрать своё',
};

/**
 * СИЯЙ в своём боте.
 *
 * Здесь мы не подрядчик, а магазин: витрина с готовым мерчем и своя
 * мастерская принта. Обложки нет — человек пришёл в бот сам, объяснять
 * ему, куда он попал, поздно.
 */
const SIYAI: Brand = {
  designWidth: 1160,
  designHeight: 705,
  mobileWidth: 390,
  mobileHeight: 780,
  shell: 'compact',

  // Витрина не урезается: продаём и худи, и кепки, и аксессуары.
  // А собрать своё можно пока только на футболке — см. customGarments.
  customGarments: ['tshirt'],
  headerCart: true,
  logoUrl: '/siyai/logo.png',
  logoMiniUrl: '/siyai/logo.png',
  splashLogoUrl: '/siyai/logo-3d.webp',
  printPriceRub: 3000,
  textFonts: [
    { key: 'manrope', title: 'Manrope', family: 'Manrope', weight: 800 },
    { key: 'playfair', title: 'Playfair', family: '"Playfair Display"', weight: 700 },
    { key: 'unbounded', title: 'Unbounded', family: 'Unbounded', weight: 800 },
  ],
  coverTitle: 'Собери свой мерч',
  shopTitle: 'Каталог',
};

const BRANDS: Record<string, Brand> = {
  fosy: FOSY,
  siyai: SIYAI,
};

/** Ключ принта «для всех остальных цветов». */
const ANY_COLOR = '*';

/**
 * Принт готового изделия для этого цвета ткани.
 *
 * Возвращает пусто, если у заказчика принта нет вовсе: тогда витрина
 * показывает чистое изделие, и это честнее выдуманной картинки.
 */
export function shopPrintFor(brand: Brand, color: string): string | undefined {
  return brand.shopPrints?.[color] ?? brand.shopPrints?.[ANY_COLOR];
}

export function brandFor(slug: string): Brand {
  return BRANDS[slug] ?? RECORDS;
}
